import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarIcon } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CompanyKpis, KPI_CUSTOMERS_CARD_KEY } from '../components/CompanyKpis.tsx';
import { MetricCard } from '../components/MetricCard';
import { useAuth } from '../contexts/AuthContext';
import { useMetrics } from '../hooks/useMetrics';
import { usePermissions } from '../hooks/usePermissions.ts';
import {
  asOfParamToCalendarDate,
  dateToAsOfParam,
  formatAsOfLabel,
  isValidAsOfParam,
  utcTodayParam,
} from '../lib/asOfDate.ts';
import { cn } from '../lib/utils.ts';
import {
  dashboardCardsForTab,
  type DashboardTab,
  DEFAULT_DASHBOARD_TAB,
  parseDashboardTab,
  visibleDashboardTabs,
} from './dashboardMetrics.ts';

export function DashboardPage() {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Point-in-time view: ?asOf=YYYY-MM-DD (UTC day), shareable/bookmarkable.
  // Garbage, future dates, and the still-in-progress current UTC day all
  // fall back to the live view rather than an error or an all-blank grid.
  const todayUtc = utcTodayParam();
  const rawAsOf = searchParams.get('asOf');
  const asOf = rawAsOf !== null && isValidAsOfParam(rawAsOf) && rawAsOf < todayUtc
    ? rawAsOf
    : undefined;

  // The selected tab rides the same URL (?tab=) so a shared link or screenshot
  // reproduces exactly; Home is the default and stays out of the URL. Only
  // tabs the user may see are offered or parsed (Spend is Admin-tier).
  const visibleTabs = visibleDashboardTabs(hasPermission);
  const tab = parseDashboardTab(searchParams.get('tab'), visibleTabs);

  const { metrics, isLoading, error } = useMetrics(asOf);

  const setAsOfParam = (value: string | undefined) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      // The current UTC day (and clearing) means the live dashboard — no
      // param. Compared as UTC, not local: a UTC+X user's "yesterday" can be
      // the UTC day still in progress, whose rows don't exist yet.
      if (value === undefined || value >= todayUtc) {
        next.delete('asOf');
      } else {
        next.set('asOf', value);
      }
      return next;
    }, { replace: true });
  };

  const setTabParam = (value: DashboardTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === DEFAULT_DASHBOARD_TAB) {
        next.delete('tab');
      } else {
        next.set('tab', value);
      }
      return next;
    }, { replace: true });
  };

  // Format timestamp for display
  const lastUpdated = metrics?.timestamp ? new Date(metrics.timestamp).toLocaleString() : null;

  return (
    <div className='space-y-8'>
      {/* Welcome — brand-aligned greeting: small eyebrow + Titillium heading */}
      <div>
        <p className='metron-eyebrow'>Dashboard</p>
        <h1 className='font-heading text-3xl font-normal tracking-tight mt-1'>
          Welcome back, {user?.name || user?.email || 'User'}.
        </h1>
      </div>

      {/* Metrics section */}
      <div>
        <div className='mb-4 flex items-start justify-between gap-4 border-b'>
          <div role='tablist' aria-label='Dashboard sections' className='flex items-center gap-1'>
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              const selected = t.id === tab;
              return (
                <button
                  key={t.id}
                  type='button'
                  role='tab'
                  id={`dashboard-tab-${t.id}`}
                  aria-selected={selected}
                  aria-controls={selected ? 'dashboard-tabpanel' : undefined}
                  onClick={() => setTabParam(t.id)}
                  className={cn(
                    '-mb-px inline-flex h-9 items-center gap-2 border-b-2 px-3 text-sm font-medium transition-colors',
                    selected
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  {Icon
                    ? (
                      <>
                        <Icon className='h-4 w-4' />
                        <span className='sr-only'>{t.label}</span>
                      </>
                    )
                    : t.label}
                </button>
              );
            })}
          </div>
          <div className='flex flex-col items-end gap-1 pb-2'>
            <div className='flex items-center gap-2'>
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  {asOf
                    ? (
                      <Button variant='outline' size='sm' className='gap-2 h-8'>
                        <CalendarIcon className='h-4 w-4' />
                        {formatAsOfLabel(asOf)}
                      </Button>
                    )
                    : (
                      <Button
                        variant='outline'
                        size='sm'
                        className='h-8 w-8 p-0'
                        aria-label='View metrics as of a date'
                      >
                        <CalendarIcon className='h-4 w-4' />
                      </Button>
                    )}
                </PopoverTrigger>
                <PopoverContent className='w-auto p-0' align='end'>
                  <Calendar
                    mode='single'
                    defaultMonth={asOf ? asOfParamToCalendarDate(asOf) : undefined}
                    selected={asOf ? asOfParamToCalendarDate(asOf) : undefined}
                    disabled={{ after: asOfParamToCalendarDate(todayUtc) }}
                    onSelect={(day: Date | undefined) => {
                      if (day) {
                        setAsOfParam(dateToAsOfParam(day));
                        setPickerOpen(false);
                      }
                    }}
                  />
                </PopoverContent>
              </Popover>
              {asOf && (
                <Button
                  variant='outline'
                  size='sm'
                  className='h-8'
                  onClick={() => setAsOfParam(undefined)}
                >
                  Today
                </Button>
              )}
            </div>
            {!asOf && lastUpdated && (
              <p className='text-xs text-muted-foreground'>
                Updated {lastUpdated}
              </p>
            )}
          </div>
        </div>
        {/* Company KPIs (MET-3620) lead the Home tab; its customers card moves up into the row */}
        {tab === 'home' && (
          <div className='mb-4'>
            <CompanyKpis metrics={metrics ?? null} isLoading={isLoading} asOf={asOf} />
          </div>
        )}
        {/* Users is two columns so each product's daily and 30-day card share a row */}
        <div
          role='tabpanel'
          id='dashboard-tabpanel'
          aria-labelledby={`dashboard-tab-${tab}`}
          className={cn('grid gap-4 md:grid-cols-2', tab !== 'users' && 'lg:grid-cols-3')}
        >
          {dashboardCardsForTab(tab).filter((m) => m.key !== KPI_CUSTOMERS_CARD_KEY).map((m) => {
            const Icon = m.icon;
            return (
              <MetricCard
                key={m.key}
                title={m.title}
                value={m.getValue(metrics ?? null)}
                breakdown={m.getBreakdown?.(metrics ?? null)}
                icon={<Icon className='h-4 w-4' />}
                isLoading={isLoading}
                description={m.description}
                timestamp={m.getTimestamp(metrics ?? null)}
                valueClassName={m.getValueClassName?.(metrics ?? null)}
                metricId={m.metricId}
                granularity={m.granularity}
                showHistory={m.showHistory}
                yDomainMode={m.yDomainMode}
                valueFormatter={m.valueFormatter}
                baseline={m.baseline}
                target={m.target}
                rollingSumDays={m.rollingSumDays}
                lowerIsBetter={m.lowerIsBetter}
                asOf={asOf}
              />
            );
          })}
        </div>
      </div>

      {error && (
        <Card className='border-destructive'>
          <CardContent className='pt-6'>
            <p className='text-sm text-destructive'>Failed to load metrics: {error}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
